// Punkt 1 aus docs/design/live-verlauf.md: Geräteseite und Übersicht sollen
// sich von selbst auffrischen.
//
// Die Zentrale schickt über Server-Sent Events nur Hinweise ("zu diesem Gerät
// gibt es etwas Neues"), keine Messwerte. Was neu ist, holt die Seite über
// ihre übliche Abfrage — so bleibt die Rechteprüfung dort, wo sie schon
// steht, und ein verpasster Hinweis ist harmlos.
import { useEffect, useRef } from 'react'

/** Wie lange nach einem Abriss gewartet wird, ehe neu verbunden wird. */
export const STREAM_RETRY_MS = 5_000

/**
 * Hört auf Änderungen und ruft `onChange` auf. `geraet` grenzt den Strom auf
 * ein Gerät ein; ohne Angabe kommen alle sichtbaren.
 *
 * Kennt der Browser keine EventSource (jsdom in den Tests), passiert nichts:
 * die Seiten fragen ohnehin in Abständen nach.
 */
export function useAenderungen(onChange: () => void, geraet?: string): void {
  const melden = useRef(onChange)
  melden.current = onChange

  useEffect(() => {
    if (typeof EventSource === 'undefined') return
    let quelle: EventSource | null = null
    let neustart: ReturnType<typeof setTimeout> | null = null
    let beendet = false

    const verbinden = () => {
      if (beendet) return
      const pfad = geraet ? `/api/stream?geraet=${encodeURIComponent(geraet)}` : '/api/stream'
      quelle = new EventSource(pfad, { withCredentials: true })
      quelle.addEventListener('device', () => melden.current())
      quelle.addEventListener('overview', () => melden.current())
      quelle.onerror = () => {
        // EventSource verbindet selbst neu, aber nicht nach jedem Fehler.
        // Ein eigener Versuch nach fünf Sekunden ist billiger als eine
        // Seite, die stillschweigend nicht mehr aktualisiert.
        quelle?.close()
        quelle = null
        if (!beendet) neustart = setTimeout(verbinden, STREAM_RETRY_MS)
      }
    }
    verbinden()

    return () => {
      beendet = true
      if (neustart) clearTimeout(neustart)
      quelle?.close()
    }
  }, [geraet])
}
